/**
 * ADR-0212 — subject-area access on screen: the sentences the designer and the
 * People page show, and the restriction edits the designer makes. Pure
 * functions, so they can be tested without a React renderer (the standing
 * console constraint noted in `semanticTemplateLayer.ts`).
 *
 * WHAT THE SCREENS RECORD. A column's own access (the floor) is allow or
 * restrict. A non-Master subject area may additionally restrict a column the
 * object allows — the field's id in that area's placement of the object
 * (`restricted_field_ids`). People are given areas on the People page. None of
 * it restricts anyone until access by subject area ships; the hub enforces the
 * rules again on every write.
 *
 * Every sentence here is spec §4.7's, word for word, except that a count of
 * one reads "1 person" rather than "1 people".
 */

/** One area of one model, as `GET /api/tenant/admin/subject-areas` serves it. */
export type AdminSubjectArea = {
  id: string;
  name: string;
  model_id: string;
  model_name: string;
  is_master: boolean;
  object_count: number;
  restricted_column_count: number;
  assigned: AssignedPerson[];
};

export type AssignedPerson = { account_id: string; display_name: string | null; email: string };

export const MASTER_NOT_ASSIGNABLE = "Master holds every object and cannot be assigned.";

/** A person's name; the email only for someone with no display name — never nothing (spec E10). */
export function personLabel(p: { display_name: string | null; email: string }): string {
  return p.display_name?.trim() || p.email;
}

/** The Subject area tab's one read-only line (§4.3). */
export function assignedLine(isMaster: boolean, assigned: readonly AssignedPerson[]): string {
  if (isMaster) return MASTER_NOT_ASSIGNABLE;
  if (assigned.length === 0) return "Assigned to nobody yet.";
  const names = assigned.map(personLabel).join(", ");
  return assigned.length === 1 ? `Assigned to 1 person: ${names}` : `Assigned to ${assigned.length} people: ${names}`;
}

/** The sentence a deleting area's confirmation gains when people hold it (§4.3), or null. */
export function areaDeletionWarning(assigned: readonly AssignedPerson[]): string | null {
  if (assigned.length === 0) return null;
  const names = assigned.map(personLabel).join(", ");
  return assigned.length === 1
    ? `1 person is assigned this area and will lose it: ${names}.`
    : `${assigned.length} people are assigned this area and will lose it: ${names}.`;
}

type AccessField = { id: string; security_profile?: { security_class?: string } };
type AccessPlacement = { object_id: string; restricted_field_ids?: string[] };
type AccessArea = { id: string; is_master?: boolean; object_placements: AccessPlacement[] };

/** How a field is restricted in an area: by its own setting everywhere, by this area, or not at all. */
export type FieldRestriction = "everywhere" | "here" | null;

export function fieldRestriction(area: AccessArea | undefined, objectId: string, field: AccessField): FieldRestriction {
  if (field.security_profile?.security_class === "restrict") return "everywhere";
  if (!area || area.is_master) return null;
  const placement = area.object_placements.find((p) => p.object_id === objectId);
  return placement?.restricted_field_ids?.includes(field.id) ? "here" : null;
}

/**
 * The floor as a non-Master area view shows it — read-only text, never a
 * control. The floor is restricted everywhere and no area re-opens it (R12),
 * so an area view edits only its own restriction; the floor is set in Master,
 * where the word means what it says. (Added 2026-10-21: the owner set a
 * column's floor from an area view, meaning the area, and lost the column
 * model-wide.)
 */
export const FLOOR_SET_IN_MASTER = "Set in Master.";

export function floorAccessLabel(field: AccessField): "Allow" | "Restrict" {
  return field.security_profile?.security_class === "restrict" ? "Restrict" : "Allow";
}

/**
 * Set or clear one area's restriction of one column (§4.3, C2): the field's
 * id goes into or out of that area's placement of the object. Returns the
 * next spec (the input is not mutated), or null when nothing can change — a
 * Master area, an object the area does not place, or no change.
 */
export function setAreaRestriction<S extends { subject_areas: AccessArea[] }>(
  spec: S,
  areaId: string,
  objectId: string,
  fieldId: string,
  restricted: boolean
): S | null {
  const next = JSON.parse(JSON.stringify(spec)) as S;
  const area = next.subject_areas.find((a) => a.id === areaId);
  if (!area || area.is_master) return null;
  const placement = area.object_placements.find((p) => p.object_id === objectId);
  if (!placement) return null;
  const ids = placement.restricted_field_ids ?? [];
  const has = ids.includes(fieldId);
  if (has === restricted) return null;
  const kept = restricted ? [...ids, fieldId] : ids.filter((id) => id !== fieldId);
  if (kept.length === 0) delete placement.restricted_field_ids;
  else placement.restricted_field_ids = kept;
  return next;
}

/** §3.7 — a note stays a canvas object, never a document. */
export const NOTE_MAX_CHARS = 2000;

/** Show the inspector's countdown once a note is within this many characters of the cap. */
export const NOTE_COUNTDOWN_FROM = 200;

/** "‹n› characters left" near the cap, or null while there is room. */
export function noteCountdown(text: string): string | null {
  const left = NOTE_MAX_CHARS - text.length;
  if (left > NOTE_COUNTDOWN_FROM) return null;
  return left === 1 ? "1 character left" : `${Math.max(0, left)} characters left`;
}

/**
 * The People page picker's candidates (§4.5): every non-Master area, by name,
 * with the model's name only when the tenant has more than one model.
 */
export function areaCandidates(areas: readonly AdminSubjectArea[]): Array<{ id: string; label: string }> {
  const models = new Set(areas.map((a) => a.model_id));
  return areas
    .filter((a) => !a.is_master)
    .map((a) => ({ id: a.id, label: models.size > 1 ? `${a.name} (${a.model_name})` : a.name }))
    .sort((x, y) => x.label.localeCompare(y.label));
}
